import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, Shield, AlertTriangle, ChevronRight, ChevronLeft, AlertCircle
} from 'lucide-react';

import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import FormStepper from '../common/FormStepper';
import { useToast } from '../common/Toast';
import complianceService from '../../services/complianceService';

//////////////////////////////////////////////////////////////
// STEPS (LIKE RFP)
//////////////////////////////////////////////////////////////

const STEPS = [
  { id: 0, label: 'Framework Info', icon: Shield },
  { id: 1, label: 'Details', icon: AlertCircle },
  { id: 2, label: 'Requirements', icon: AlertTriangle },
];

const FrameworkForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'Regulatory',
    requirements: []
  });

  useEffect(() => {
    if (id) loadFramework(id);
  }, [id]);

  const loadFramework = async (frameworkId) => {
    setLoading(true);
    try {
      const res = await complianceService.getFrameworks();
      const found = res.data.find(f => f._id === frameworkId || f.id === frameworkId);
      if (found) setFormData(found);
    } catch (err) {
      showError('Failed to load framework');
    } finally {
      setLoading(false);
    }
  };

  //////////////////////////////////////////////////////////////
  // SUBMIT
  //////////////////////////////////////////////////////////////

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (id) {
        await complianceService.updateFramework(id, formData);
        success('Framework updated successfully');
      } else {
        await complianceService.createFramework(formData);
        success('Framework created successfully');
      }
      navigate('/contracts/frameworks');
    } catch (err) {
      showError(err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  //////////////////////////////////////////////////////////////
  // NAVIGATION
  //////////////////////////////////////////////////////////////

  const handleNext = (e) => {
    e.preventDefault();
    if (currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  //////////////////////////////////////////////////////////////
  // STEP CONTENT
  //////////////////////////////////////////////////////////////

const step0 = () => (
  <>
    <div className="mb-4">
      <label className="form-label">Framework Name *</label>
      <input
        className="form-control"
        placeholder="e.g. GDPR"
        value={formData.name}
        onChange={e => setFormData({ ...formData, name: e.target.value })}
      />
    </div>

    <div className="mb-4">
      <label className="form-label">Framework Code *</label>
      <input
        className="form-control"
        placeholder="e.g. EU-GDPR"
        value={formData.code}
        onChange={e =>
          setFormData({
            ...formData,
            code: e.target.value.toUpperCase()
          })
        }
      />
    </div>
  </>
);

  const step1 = () => (
    <>
      <div className="mb-4">
        <label className="form-label">Type</label>
        <select
          className="form-select"
          value={formData.type}
          onChange={e => setFormData({ ...formData, type: e.target.value })}
        >
          <option>Regulatory</option>
          <option>Industry Standard</option>
          <option>Internal Policy</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
        />
      </div>
    </>
  );

  const step2 = () => (
    <>
      {!id ? (
        <div className="text-muted">
          Save framework first to add requirements.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {formData.requirements.map((req, i) => (
                <tr key={i}>
                  <td>{req.code}</td>
                  <td>{req.description}</td>
                  <td>{req.riskLevel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const stepsContent = [step0, step1, step2];

  //////////////////////////////////////////////////////////////
  // RENDER
  //////////////////////////////////////////////////////////////

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title={id ? 'Edit Framework' : 'Create Framework'}
        description="Framework creation with structured steps"
      />

      <div style={{ padding: '24px' }}>

        <div style={{
          background: 'white',
          border: '1px solid var(--border-color)'
        }}>

          {/* STEPPER */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={(i) => setCurrentStep(i)}
          />

          <div style={{ padding: '32px' }}>
            <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}>
              
              {stepsContent[currentStep]()}

              {/* NAV BUTTONS */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '40px'
              }}>
                
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/contracts/frameworks')}
                  >
                    Cancel
                  </button>

                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="btn btn-secondary ms-2"
                      onClick={handleBack}
                    >
                      <ChevronLeft size={16} /> Back
                    </button>
                  )}
                </div>

                <div>
                  {currentStep < STEPS.length - 1 ? (
                    <button type="submit" className="btn btn-primary">
                      Next <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      <Save size={16} /> Save
                    </button>
                  )}
                </div>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FrameworkForm;